import { useEffect } from 'react'
import {
	type ErrorResponse,
	isRouteErrorResponse,
	useParams,
	useRouteError,
} from 'react-router'
import { getErrorMessage } from '#app/utils/misc.tsx'

type StatusHandler = (info: {
	error: ErrorResponse
	params: Record<string, string | undefined>
}) => React.JSX.Element | null

export function GeneralErrorBoundary({
	defaultStatusHandler = ({ error }) => (
		<p>
			{error.status} {error.data}
		</p>
	),
	statusHandlers,
	unexpectedErrorHandler = error => <p>{getErrorMessage(error)}</p>,
}: {
	defaultStatusHandler?: StatusHandler
	statusHandlers?: Record<number, StatusHandler>
	unexpectedErrorHandler?: (error: unknown) => React.JSX.Element | null
}) {
	const error = useRouteError()
	const params = useParams()

	useEffect(() => {
		console.error(error)
		// Report unexpected (non-Response) errors to Sentry when it is enabled.
		// The SDK is loaded lazily so it stays out of the bundle otherwise.
		if (!isRouteErrorResponse(error) && window.ENV?.SENTRY_DSN) {
			void import('@sentry/react-router').then(Sentry =>
				Sentry.captureException(error),
			)
		}
	}, [error])

	return (
		<div className="container flex items-center justify-center p-20 text-h2">
			{isRouteErrorResponse(error)
				? (statusHandlers?.[error.status] ?? defaultStatusHandler)({
						error,
						params,
					})
				: unexpectedErrorHandler(error)}
		</div>
	)
}
